import { getConfirmedRoleDataFromCache } from 'src/components/Profile/CareerHistory/apolloCache.optimistic';
import { getUnconfirmedRolesFromCache } from 'src/components/Profile/CareerHistory/data';
import {
  CreateResumeActions,
  CreateStep,
} from 'src/components/Profile/CreateResume/reducer';
import {
  getConfirmedQualificationsFromCache,
  getUnconfirmedQualificationsFromCache,
} from 'src/components/Profile/Education/data';
import { getLicenceDataFromStore } from 'src/components/Profile/Licences/apolloCache.ts';
import type { GraphqlLicence } from 'src/components/Profile/Licences/types.ts';
import { PROFILE_INSIGHTS_LOADED } from 'src/components/Profile/ProfileInsights/actionCreators';
import {
  Actions as ProfileVisibilityActions,
  type ProfileVisibilityAction,
} from 'src/components/Profile/ProfileVisibility/actions';
import { PROFILE_VISIBILITY_DRAWER_OPENED } from 'src/components/Profile/ProfileVisibilityV2/EditProfileVisibility/actionCreators.ts';
import { PROFILE_RESUME_DEFAULT_WITH_ALERT_LOADED } from 'src/components/Profile/Resume/EditResume/ManageResumes/ResumeDetailItem/actionCreators';
import {
  PROFILE_SUGGESTIONS_CTA_PRESSED,
  PROFILE_SUGGESTIONS_LOADED,
} from 'src/components/Profile/Suggestions/actionCreators';
import { getCandidateIdFromCache } from 'src/components/Profile/data';
import { getUserDetailsFromCache } from 'src/handlers/analytics';
import type { createAnalytics } from 'src/modules/analytics';
import type { ProfileState } from 'src/modules/analytics/reducers/profile';
import type {
  AnalyticsLinkEvent,
  AnalyticsLinkPosition,
  AnalyticsHeaderFooterLinkProps,
  AnalyticsProfileSuggestionsProps,
  AnalyticsResumeAlertShownType,
  AnalyticsResumeAlertShownTone,
  AnalyticsResumeAlertPosition,
  AnalyticsResumeAlertShownMessage,
  AnalyticsResumeAlertTitle,
} from 'src/modules/analytics/types';
import type { CardOpenProps } from 'src/modules/analytics/types/cardOpened';
import { getDataFromCache } from 'src/modules/apollo/apolloCache';
import type { AppConfig } from 'src/modules/appConfig/types';
import {
  COMPANY_REVIEWS_LINK_CLICKED,
  HEADER_FOOTER_LINK_CLICKED,
  TRACKED_LINK_CLICKED,
} from 'src/reducers/location';
import type { Action, ApiClient } from 'src/types';
import {
  GetApproachabilityDocument,
  GetProfileInsightsDocument,
  GetProfileVisibility2Document,
  GetReferenceChecksDocument,
  GetVerificationsDocument,
} from 'src/types/graphql';

import { instrumentAnalytics } from './analytics/analyticsMiddlewareUtils';
import { ProfileActions } from './analytics/profile/profile.ts';
import {
  calculateLicenceAnalytics,
  calculateQualificationAnalytics,
  calculateVerificationsAnalytics,
} from './utils';

type AnalyticsMiddlewareAction =
  | (Action<CreateResumeActions.UPDATE_CREATE_STEP> & {
      payload: { from: CreateStep; to: CreateStep };
    })
  | Extract<
      ProfileVisibilityAction,
      Action<ProfileVisibilityActions.PROFILE_VISIBILITY_UPDATE_START>
    >
  | (Action<typeof COMPANY_REVIEWS_LINK_CLICKED> & {
      payload: {
        companyName: string;
      };
    })
  | (Action<typeof TRACKED_LINK_CLICKED> & {
      payload: {
        analytics: {
          linkName: AnalyticsLinkEvent;
          thirdPartyLink: boolean;
          linkPosition: AnalyticsLinkPosition;
        };
        type: string;
      };
    })
  | (Action<typeof HEADER_FOOTER_LINK_CLICKED> & {
      payload: AnalyticsHeaderFooterLinkProps;
    })
  | Action<typeof PROFILE_VISIBILITY_DRAWER_OPENED>
  | Action<typeof PROFILE_INSIGHTS_LOADED>
  | (Action<typeof PROFILE_SUGGESTIONS_LOADED> & {
      payload: AnalyticsProfileSuggestionsProps;
    })
  | (Action<typeof PROFILE_SUGGESTIONS_CTA_PRESSED> & {
      payload: AnalyticsProfileSuggestionsProps;
    })
  | Action<CreateResumeActions.CREATE_RESUME_RENDERED>
  | Action<ProfileActions.PROFILE_RENDERED>
  | Action<ProfileVisibilityActions.PROFILE_VISIBILITY_OPENED>
  | (Action<typeof PROFILE_RESUME_DEFAULT_WITH_ALERT_LOADED> & {
      payload: {
        alertShownType: AnalyticsResumeAlertShownType;
        alertShownTone: AnalyticsResumeAlertShownTone;
        alertShownPosition: AnalyticsResumeAlertPosition;
        alertShownTitle: AnalyticsResumeAlertTitle;
        alertShownMessage: AnalyticsResumeAlertShownMessage;
      };
    });

export type Analytics = ReturnType<typeof createAnalytics>;

export type AnalyticsEditLoadedType = Pick<
  CardOpenProps,
  'currentPageSectionState' | 'customProps' | 'currentPageSubsection'
>;

export const createAnalyticsMiddleware = (
  analytics: Analytics,
  { apolloClient }: ApiClient,
  { language: languageCode, zone, isUnifiedSite, locale }: AppConfig,
) =>
  instrumentAnalytics(analytics) &&
  (() =>
    (next: (action: AnalyticsMiddlewareAction) => void) =>
    (action: AnalyticsMiddlewareAction) => {
      const {
        profileVisibilityUpdate,
        profileLoaded,
        resumeAlertViewed,
        profileInsightsAnalyticsTracker,
        profileVisibilityAnalyticsTracker,
        profileSuggestionsAnalyticsTracker,
        createResumeLoaded,
        createResumeButtonClicked,
        linkClicked,
        headerFooterLinkClicked,
        companyReviewsClicked,
      } = analytics;

      // PROFILE_PAGE;

      const getProfileVisibilityLevelData = () =>
        getDataFromCache({
          cache: apolloClient.cache,
          query: GetProfileVisibility2Document,
          variables: {
            locale: `${languageCode}-${locale}`,
          },
        });

      const getProfileApproachabilityData = () =>
        getDataFromCache({
          cache: apolloClient.cache,
          query: GetApproachabilityDocument,
        });

      const getProfileInsightsData = () =>
        getDataFromCache({
          cache: apolloClient.cache,
          query: GetProfileInsightsDocument,
          variables: {
            zone,
            languageCode,
            timezone: 'Australia/Melbourne',
            locale: `${languageCode}-${locale}`,
          },
        });

      const getGlobalProfileStateTrackingData = (): ProfileState => {
        const candidateId = getCandidateIdFromCache(apolloClient.cache);

        const profileVisibilityLevelData = getProfileVisibilityLevelData();

        const profileVisibilityId =
          profileVisibilityLevelData?.viewer?.profileVisibility2?.id;

        const profileVisibilityLevel = profileVisibilityId
          ? profileVisibilityId.toLowerCase()
          : 'not set';

        const profileApproachabilityData = getProfileApproachabilityData();

        const approachable =
          profileApproachabilityData?.viewer?.approachability?.approachable ??
          true;

        const profileApproachabilityStatus = approachable ? 'on' : 'off';

        const profileInsights =
          getProfileInsightsData()?.viewer?.profileInsights2;

        return {
          profileVisibilityLevel,
          profileApproachabilityStatus,
          candidateId: candidateId ?? undefined,
          profileViewedCount: profileInsights?.viewCount ?? 0,
          profileSearchAppearanceCount:
            profileInsights?.searchImpressionCount ?? 0,
        };
      };

      switch (action.type) {
        case CreateResumeActions.CREATE_RESUME_RENDERED: {
          const { _id, trackingId, externalId } = getUserDetailsFromCache(
            apolloClient.cache,
          );

          createResumeLoaded(
            {
              eventName: 'create_resume_displayed',
              currentPage: 'create resume',
            },
            {
              trackingId,
              _id,
              externalId: externalId || undefined,
              isUnifiedSite,
            },
          );

          break;
        }

        case ProfileActions.PROFILE_RENDERED: {
          const { _id, trackingId, externalId } = getUserDetailsFromCache(
            apolloClient.cache,
          );

          const licences = getLicenceDataFromStore({
            cache: apolloClient.cache,
            languageCode,
            zone,
          });

          const { unconfirmedRoles } =
            getUnconfirmedRolesFromCache(apolloClient);

          const { unconfirmedQualifications } =
            getUnconfirmedQualificationsFromCache({
              apolloClient,
              languageCode,
              zone,
            });
          const { confirmedQualifications } =
            getConfirmedQualificationsFromCache({
              ...apolloClient,
              languageCode,
              zone,
            });

          const getProfileVerificationsData = () =>
            getDataFromCache({
              cache: apolloClient.cache,
              query: GetVerificationsDocument,
              variables: {
                zone,
                locale: `${languageCode}-${locale}`,
              },
            });

          const verifications =
            getProfileVerificationsData()?.viewer?.profileVerifications;

          const workHistory = getConfirmedRoleDataFromCache({
            cache: apolloClient.cache,
          });
          const profileCareerHistoryCount = workHistory?.length;

          const referenceChecks = getDataFromCache({
            cache: apolloClient.cache,
            query: GetReferenceChecksDocument,
            variables: {
              locale: `${languageCode}-${locale}`,
            },
          })?.viewer?.referenceChecks;

          const referencesTotal = referenceChecks
            ? referenceChecks.reduce(
                (acc, curr) => acc + curr.references.length,
                0,
              )
            : 0;

          const isResumeParsed =
            unconfirmedRoles.length > 0 || unconfirmedQualifications.length > 0;

          profileLoaded(getGlobalProfileStateTrackingData(), {
            trackingId,
            _id,
            externalId: externalId || undefined,
            isResumeParsed,
            isUnifiedSite,
            ...calculateLicenceAnalytics(licences as GraphqlLicence[]),
            ...(confirmedQualifications.length > 0
              ? calculateQualificationAnalytics(confirmedQualifications)
              : {}),
            ...calculateVerificationsAnalytics(
              verifications?.verified,
              verifications?.verifiable,
            ),
            profileCareerHistoryCount,
            referencesTotal,
          });
          break;
        }

        case PROFILE_INSIGHTS_LOADED: {
          profileInsightsAnalyticsTracker({
            eventName: 'profile_insights_widget_loaded',
            profileState: getGlobalProfileStateTrackingData(),
          });

          break;
        }
        case PROFILE_VISIBILITY_DRAWER_OPENED: {
          profileVisibilityAnalyticsTracker({
            eventName: 'profile_section_displayed',
            props: {
              currentPage: 'profile',
              currentPageSection: 'visibility settings',
              currentPageSectionState: 'edit',
            },
            profileState: getGlobalProfileStateTrackingData(),
          });

          break;
        }

        case PROFILE_SUGGESTIONS_LOADED: {
          const props = action.payload;
          profileSuggestionsAnalyticsTracker({
            eventName: 'nudge_viewed',
            profileState: getGlobalProfileStateTrackingData(),
            props,
          });

          break;
        }
        case PROFILE_SUGGESTIONS_CTA_PRESSED: {
          const props = action.payload;
          profileSuggestionsAnalyticsTracker({
            eventName: 'nudge_pressed',
            props,
          });

          break;
        }

        case PROFILE_RESUME_DEFAULT_WITH_ALERT_LOADED: {
          const props = action.payload;
          resumeAlertViewed({
            ...props,
            profileState: getGlobalProfileStateTrackingData(),
          });

          break;
        }

        case CreateResumeActions.UPDATE_CREATE_STEP: {
          const { to: newCreateStep } = action.payload;
          if (newCreateStep === CreateStep.PROFILE_REVIEW) {
            createResumeButtonClicked();
          }
          break;
        }

        case ProfileVisibilityActions.PROFILE_VISIBILITY_UPDATE_START: {
          const { level } = action.payload;

          const profileVisibilityLevel = level.toLowerCase();

          profileVisibilityUpdate(profileVisibilityLevel);
          break;
        }

        case COMPANY_REVIEWS_LINK_CLICKED: {
          const { companyName } = action.payload;

          companyReviewsClicked({
            linkNameEventName: 'company_reviews_pressed',
            currentPageSection: 'career history',
            companyName,
          });

          break;
        }

        case TRACKED_LINK_CLICKED: {
          const {
            analytics: { linkName, linkPosition, thirdPartyLink },
            type: linkType,
          } = action.payload;
          linkClicked({
            linkNameEventName: linkName,
            linkPosition,
            thirdPartyLink,
            type: linkType,
          });
          break;
        }

        case HEADER_FOOTER_LINK_CLICKED: {
          const { linkPosition, linkText, linkAction } = action.payload;

          headerFooterLinkClicked({
            linkNameEventName: 'navigation_pressed',
            linkPosition,
            linkText,
            linkAction,
          });
          break;
        }

        default: {
          break;
        }
      }

      return next(action);
    });
